/**
 * Play-once SplitText prose-block reveal — pin + headline (+ aside) → prose →
 * optional CTA stagger. Caller attaches ScrollTrigger (see ProseBlockReveal).
 */

import { proseBlockReveal } from '@/config/prose-block-reveal';
import { HERO_SCENE_INTRO_ID } from '@/constants/heroScene';
import {
  PROSE_BLOCK_ACTION_SELECTOR,
  PROSE_BLOCK_ASIDE_SELECTOR,
  PROSE_BLOCK_HEADLINE_SELECTOR,
  PROSE_BLOCK_PIN_SELECTOR,
  PROSE_BLOCK_PROSE_SELECTOR,
} from '@/constants/proseBlockReveal';
import { shouldRevealHeadlineByWord } from '@/motion/headlineRevealUnits';

import type { ScrollSceneRuntime } from '@/motion/scrollScene';

type ProseBlockRevealOptions = {
  /** Query scope — never document-wide, so multiple blocks don't clash. */
  root: HTMLElement;
};

export type ProseBlockRevealResult = {
  timeline: gsap.core.Timeline | null;
  cleanup: () => void;
};

function clearWillChange(elements: Element[]) {
  for (const el of elements) {
    if (el instanceof HTMLElement) {
      el.style.removeProperty('will-change');
    }
  }
}

function setWillChange(elements: Element[]) {
  for (const el of elements) {
    if (el instanceof HTMLElement) {
      el.style.setProperty('will-change', 'transform');
    }
  }
}

function headlineTransformOrigin(): string {
  return document.documentElement.dir === 'rtl'
    ? proseBlockReveal.headline.transformOriginRtl
    : proseBlockReveal.headline.transformOriginLtr;
}

/**
 * Build a timed (non-scrubbed) timeline: pin + SplitText headline (+ aside) at
 * t0 → prose fade → action stagger. ScrollTrigger is attached by the caller.
 */
export function createProseBlockReveal(
  { gsap, SplitText }: ScrollSceneRuntime,
  { root }: ProseBlockRevealOptions,
): ProseBlockRevealResult {
  const pinTargets = gsap.utils.toArray<HTMLElement>(
    root.querySelectorAll(PROSE_BLOCK_PIN_SELECTOR),
  );
  const headlineEls = gsap.utils.toArray<HTMLElement>(
    root.querySelectorAll(PROSE_BLOCK_HEADLINE_SELECTOR),
  );
  const asideTargets = gsap.utils.toArray<HTMLElement>(
    root.querySelectorAll(PROSE_BLOCK_ASIDE_SELECTOR),
  );
  const proseTargets = gsap.utils.toArray<HTMLElement>(
    root.querySelectorAll(PROSE_BLOCK_PROSE_SELECTOR),
  );
  const actionTargets = gsap.utils.toArray<HTMLElement>(
    root.querySelectorAll(PROSE_BLOCK_ACTION_SELECTOR),
  );

  if (
    pinTargets.length === 0 &&
    headlineEls.length === 0 &&
    asideTargets.length === 0 &&
    proseTargets.length === 0 &&
    actionTargets.length === 0
  ) {
    return { timeline: null, cleanup: () => undefined };
  }

  let split: InstanceType<ScrollSceneRuntime['SplitText']> | null = null;
  let unitsByLine: Element[][] = [];
  let allHeadlineUnits: Element[] = [];

  if (headlineEls.length > 0) {
    const revealByWord = shouldRevealHeadlineByWord();
    split = SplitText.create(headlineEls, {
      type: revealByWord ? 'words,lines' : 'chars,words,lines',
      mask: proseBlockReveal.headline.mask,
      aria: 'auto',
    });
    allHeadlineUnits = revealByWord ? split.words : split.chars;
    unitsByLine = headlineEls.map((el) =>
      allHeadlineUnits.filter((unit) => el.contains(unit)),
    );
    // Clear CSS pre-hide on the parent; units stay invisible until the segment.
    gsap.set(headlineEls, { autoAlpha: 1, y: 0 });
    for (const units of unitsByLine) {
      gsap.set(units, {
        transformOrigin: headlineTransformOrigin(),
        autoAlpha: 0,
        yPercent: proseBlockReveal.headline.yPercent,
        y: (i: number) => i * proseBlockReveal.headline.staggerOffsetYPx,
        rotation: proseBlockReveal.headline.rotationDeg,
      });
    }
  }

  // Intro part2 lives under the hero-pinned `#introduction`. Without
  // `pinnedContainer`, ScrollTrigger miscalculates start and the once-play
  // fires before the block enters the viewport.
  const pinnedContainer = root.closest<HTMLElement>(`#${HERO_SCENE_INTRO_ID}`);

  const timeline = gsap.timeline({
    scrollTrigger: {
      trigger: root,
      start: proseBlockReveal.scrollTriggerStart,
      once: true,
      toggleActions: 'play none none none',
      invalidateOnRefresh: true,
      ...(pinnedContainer ? { pinnedContainer } : {}),
    },
  });
  timeline.addLabel('proseBlockHeadline', 0);
  const hasHeadlineSegment = allHeadlineUnits.length > 0 || asideTargets.length > 0;

  if (pinTargets.length > 0) {
    timeline.fromTo(
      pinTargets,
      {
        autoAlpha: 0,
        scale: proseBlockReveal.pin.fromScale,
        y: 0,
      },
      {
        autoAlpha: 1,
        scale: 1,
        y: 0,
        ease: proseBlockReveal.pin.ease,
        duration: proseBlockReveal.pin.duration,
        transformOrigin: '50% 100%',
      },
      'proseBlockHeadline',
    );
  }

  for (const units of unitsByLine) {
    if (units.length === 0) continue;
    timeline.fromTo(
      units,
      {
        autoAlpha: 0,
        yPercent: proseBlockReveal.headline.yPercent,
        y: (i: number) => i * proseBlockReveal.headline.staggerOffsetYPx,
        rotation: proseBlockReveal.headline.rotationDeg,
      },
      {
        autoAlpha: 1,
        yPercent: 0,
        y: 0,
        rotation: 0,
        ease: proseBlockReveal.headline.ease,
        duration: proseBlockReveal.headline.duration,
        stagger: {
          each: proseBlockReveal.headline.stagger,
          from: 'start',
        },
        onStart: () => setWillChange(units),
        onComplete: () => clearWillChange(units),
      },
      'proseBlockHeadline',
    );
  }

  if (asideTargets.length > 0) {
    timeline.fromTo(
      asideTargets,
      {
        autoAlpha: 0,
        y: proseBlockReveal.aside.offsetY,
        scale: proseBlockReveal.aside.fromScale,
      },
      {
        autoAlpha: 1,
        y: 0,
        scale: 1,
        ease: proseBlockReveal.aside.ease,
        duration: proseBlockReveal.aside.duration,
        transformOrigin: '50% 50%',
      },
      'proseBlockHeadline',
    );
  }

  const maxUnitsInLine = unitsByLine.reduce(
    (max, units) => Math.max(max, units.length),
    0,
  );
  const headlineSegmentDuration =
    maxUnitsInLine > 0
      ? proseBlockReveal.headline.duration +
        proseBlockReveal.headline.stagger * Math.max(0, maxUnitsInLine - 1)
      : hasHeadlineSegment
        ? proseBlockReveal.headline.duration
        : pinTargets.length > 0
          ? proseBlockReveal.pin.duration
          : 0;
  const early = Math.min(Math.max(proseBlockReveal.prose.earlyByHeadlineFraction, 0), 1);
  const proseAt =
    headlineSegmentDuration > 0 ? headlineSegmentDuration * (1 - early) : 0;

  if (proseTargets.length > 0) {
    timeline.fromTo(
      proseTargets,
      { autoAlpha: 0, y: proseBlockReveal.prose.offsetY },
      {
        autoAlpha: 1,
        y: 0,
        ease: proseBlockReveal.prose.ease,
        duration: proseBlockReveal.prose.duration,
      },
      proseAt,
    );
  }

  if (actionTargets.length > 0) {
    const actionsAt =
      proseTargets.length > 0
        ? proseAt + proseBlockReveal.prose.duration
        : proseAt + headlineSegmentDuration;

    timeline.fromTo(
      actionTargets,
      { autoAlpha: 0, y: proseBlockReveal.actions.offsetY },
      {
        autoAlpha: 1,
        y: 0,
        ease: proseBlockReveal.actions.ease,
        duration: proseBlockReveal.actions.duration,
        stagger: proseBlockReveal.actions.stagger,
      },
      actionsAt,
    );
  }

  // Play-once — restore original markup after the reveal finishes so the
  // browser is not left with SplitText wrapper nodes (scrubbed headers keep
  // the split DOM for reverse scroll and must not revert here).
  timeline.eventCallback('onComplete', () => {
    clearWillChange(allHeadlineUnits);
    if (split) {
      split.revert();
      split = null;
    }
  });

  return {
    timeline,
    cleanup: () => {
      clearWillChange(allHeadlineUnits);
      if (split) {
        split.revert();
        split = null;
      }
      timeline.scrollTrigger?.kill();
      timeline.kill();
    },
  };
}
